import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Loader2, X } from 'lucide-react';
import { SuggestionCard } from './SuggestionCard';
import { FilmplyBadge } from '../FilmplyBadge';
import type { MovieSuggestion } from '../../types/onboarding';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.filmple.io';

interface SelectedMovie {
  id: number;
  title: string;
  poster_path: string | null;
  release_date: string | null;
}

interface MovieStepProps {
  selectedGenres: number[];
  selectedMovies: SelectedMovie[];
  onAddMovie: (movie: SelectedMovie) => void;
  onRemoveMovie: (movieId: number) => void;
  maxSelections?: number;
  prefetchedSuggestions?: MovieSuggestion[] | null;
  cachedSuggestions?: MovieSuggestion[] | null;
  onSuggestionsChange?: (suggestions: MovieSuggestion[]) => void;
}

// Skeleton placeholder for streaming cards
function SuggestionSkeleton() {
  return (
    <div className="bg-[#1A1F26]/50 rounded-lg p-3 animate-pulse">
      <div className="w-24 h-36 bg-slate-700 rounded-lg mx-auto mb-2" />
      <div className="h-4 bg-slate-700 rounded w-3/4 mx-auto mb-1" />
      <div className="h-3 bg-slate-700 rounded w-1/2 mx-auto" />
    </div>
  );
}

export function MovieStep({
  selectedGenres,
  selectedMovies,
  onAddMovie,
  onRemoveMovie,
  maxSelections = 3,
  prefetchedSuggestions = null,
  cachedSuggestions = null,
  onSuggestionsChange,
}: MovieStepProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SelectedMovie[]>([]);
  const [suggestions, setSuggestions] = useState<MovieSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [streamingComplete, setStreamingComplete] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const canSelectMore = selectedMovies.length < maxSelections;
  const selectedIds = useMemo(() => selectedMovies.map((m) => m.id), [selectedMovies]);
  const prevSelectedIdsRef = useRef<number[]>([]);

  // Reset streamingComplete when selection changes (to fetch new suggestions)
  useEffect(() => {
    // Skip on initial mount
    if (prevSelectedIdsRef.current.length === 0 && selectedIds.length === 0) {
      prevSelectedIdsRef.current = selectedIds;
      return;
    }

    // Check if selectedIds actually changed
    const prevIds = prevSelectedIdsRef.current;
    const idsChanged = prevIds.length !== selectedIds.length ||
      prevIds.some((id, i) => id !== selectedIds[i]);

    if (idsChanged && initialized) {
      // Selection changed - reset to trigger new fetch
      setStreamingComplete(false);
      setSuggestions([]);
    }

    prevSelectedIdsRef.current = selectedIds;
  }, [selectedIds, initialized]);

  // Initialize from cache or prefetch on mount (prevents re-fetch on back navigation)
  useEffect(() => {
    if (initialized) return;

    // First priority: use cached suggestions (from previous visit to this step)
    if (cachedSuggestions && cachedSuggestions.length > 0) {
      setSuggestions(cachedSuggestions);
      setStreamingComplete(true);
      setInitialized(true);
      return;
    }

    // Second priority: use prefetched suggestions (from genres step)
    if (prefetchedSuggestions && prefetchedSuggestions.length > 0 && selectedMovies.length === 0) {
      setSuggestions(prefetchedSuggestions);
      setStreamingComplete(true);
      setInitialized(true);
      onSuggestionsChange?.(prefetchedSuggestions);
      return;
    }

    setInitialized(true);
  }, [cachedSuggestions, prefetchedSuggestions, selectedMovies.length, initialized, onSuggestionsChange]);

  // Fetch suggestions with SSE streaming
  const fetchSuggestionsStream = useCallback(() => {
    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    if (selectedGenres.length === 0) {
      setSuggestions([]);
      setStreamingComplete(true);
      return;
    }

    setLoadingSuggestions(true);
    setStreamingComplete(false);

    const newSuggestions: MovieSuggestion[] = [];

    const params = new URLSearchParams({
      genres: selectedGenres.join(','),
      exclude: selectedIds.join(','),
      selectedMovies: selectedIds.join(','),
    });

    const eventSource = new EventSource(`${API_URL}/api/onboarding/suggestions/movies/stream?${params}`);
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('suggestion', (event) => {
      try {
        const suggestion = JSON.parse(event.data) as MovieSuggestion;
        newSuggestions.push(suggestion);
        setSuggestions([...newSuggestions]);
        // Stop loading indicator after first suggestion arrives
        setLoadingSuggestions(false);
      } catch (err) {
        console.error('Failed to parse suggestion:', err);
      }
    });

    eventSource.addEventListener('complete', () => {
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      // Cache the suggestions for back navigation
      onSuggestionsChange?.(newSuggestions);
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.addEventListener('error', (event) => {
      console.error('SSE error:', event);
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      if (newSuggestions.length > 0) {
        onSuggestionsChange?.(newSuggestions);
      }
      eventSource.close();
      eventSourceRef.current = null;
    });

    eventSource.onerror = () => {
      // Handle connection errors
      setLoadingSuggestions(false);
      setStreamingComplete(true);
      if (newSuggestions.length > 0) {
        onSuggestionsChange?.(newSuggestions);
      }
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [selectedGenres, selectedIds, onSuggestionsChange]);

  // Trigger streaming fetch on selection change (only after initialization)
  useEffect(() => {
    // Skip if not initialized yet (using cache/prefetch instead)
    if (!initialized) return;

    // Skip if we already have suggestions and this is the initial render
    if (suggestions.length > 0 && streamingComplete) return;

    fetchSuggestionsStream();

    // Cleanup on unmount or re-fetch
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, [initialized, fetchSuggestionsStream, streamingComplete]);

  // Search movies
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoadingSearch(true);
      try {
        const params = new URLSearchParams({
          query: searchQuery,
          page: '1',
        });
        const response = await fetch(`${API_URL}/api/search/movie?${params}`);
        if (response.ok) {
          const data = await response.json();
          setSearchResults(
            (data.results || []).slice(0, 8).map((m: any) => ({
              id: m.id,
              title: m.title,
              poster_path: m.poster_path,
              release_date: m.release_date,
            }))
          );
          setShowResults(true);
        }
      } catch (err) {
        console.error('Failed to search movies:', err);
      } finally {
        setLoadingSearch(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery]);

  // Close results when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectMovie = (movie: SelectedMovie) => {
    if (selectedIds.includes(movie.id)) {
      onRemoveMovie(movie.id);
    } else if (canSelectMore) {
      onAddMovie(movie);
      setSearchQuery('');
      setShowResults(false);
    }
  };

  const getYear = (date: string | null) => (date ? date.split('-')[0] : undefined);

  // Calculate how many skeletons to show
  const skeletonsNeeded = Math.max(0, 4 - suggestions.length);

  return (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-white mb-1">Pick favorite movies</h3>
        <p className="text-sm text-slate-400">
          Up to {maxSelections} movies that define your taste
        </p>
      </div>

      {/* Search */}
      <div ref={searchRef} className="relative">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for a movie..."
            className="w-full pl-10 pr-10 py-2.5 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500"
          />
          {loadingSearch && (
            <Loader2 size={18} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 animate-spin" />
          )}
        </div>

        {/* Search results dropdown */}
        {showResults && searchResults.length > 0 && (
          <div className="absolute z-10 mt-1 w-full bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-2xl shadow-black/50 max-h-64 overflow-y-auto">
            {searchResults.map((movie) => {
              const isSelected = selectedIds.includes(movie.id);
              return (
                <button
                  key={movie.id}
                  type="button"
                  onClick={() => handleSelectMovie(movie)}
                  disabled={!isSelected && !canSelectMore}
                  className={`w-full flex items-center gap-3 p-2 hover:bg-slate-700 transition-colors ${
                    isSelected ? 'bg-cyan-500/10' : ''
                  } ${!isSelected && !canSelectMore ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="w-8 h-12 bg-slate-700 rounded overflow-hidden flex-shrink-0">
                    {movie.poster_path && (
                      <img
                        src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                        alt={movie.title}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm text-white line-clamp-1">{movie.title}</p>
                    {movie.release_date && (
                      <p className="text-xs text-slate-400">{getYear(movie.release_date)}</p>
                    )}
                  </div>
                  {isSelected && <span className="text-cyan-400 text-xs">Selected</span>}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Selected movies */}
      {selectedMovies.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm text-slate-400">
            Selected ({selectedMovies.length}/{maxSelections}):
          </p>
          <div className="flex flex-wrap gap-2">
            {selectedMovies.map((movie) => (
              <span
                key={movie.id}
                className="flex items-center gap-1 px-2 py-1 bg-cyan-500/20 text-cyan-400 text-sm rounded-lg"
              >
                {movie.title}
                <button
                  type="button"
                  onClick={() => onRemoveMovie(movie.id)}
                  className="hover:text-white"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {selectedGenres.length > 0 && (
        <div className="space-y-3">
          {loadingSuggestions && suggestions.length === 0 ? (
            <FilmplyBadge thinking />
          ) : (
            <FilmplyBadge text="thinks you'd like:" />
          )}

          {loadingSuggestions && suggestions.length === 0 ? (
            <div className="flex justify-center py-6">
              <div className="flex gap-1">
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-teal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          ) : suggestions.length > 0 || !streamingComplete ? (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {suggestions.slice(0, 4).map((movie) => (
                <SuggestionCard
                  key={movie.id}
                  type="movie"
                  id={movie.id}
                  title={movie.title}
                  posterPath={movie.poster_path}
                  releaseYear={getYear(movie.release_date)}
                  reason={movie.reason}
                  isSelected={selectedIds.includes(movie.id)}
                  onToggle={() =>
                    handleSelectMovie({
                      id: movie.id,
                      title: movie.title,
                      poster_path: movie.poster_path,
                      release_date: movie.release_date,
                    })
                  }
                />
              ))}
              {/* Show skeletons for remaining slots while streaming */}
              {!streamingComplete && skeletonsNeeded > 0 &&
                Array.from({ length: skeletonsNeeded }).map((_, i) => (
                  <SuggestionSkeleton key={`skeleton-${i}`} />
                ))
              }
            </div>
          ) : (
            <p className="text-center text-slate-500 py-4">No suggestions available</p>
          )}
        </div>
      )}
    </div>
  );
}
